import React, { createContext, useContext, useEffect, useRef, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@careercard/convex';
import { createLogger } from '../utils/logger-ssr-safe';
import { useAuth } from './AuthContext';

const logger = createLogger({ module: 'CareerCard', level: 'info' });

interface SchemaField {
  type: string;
  label?: string;
  order?: number;
  fields?: string[];
  [key: string]: unknown;
}

interface CardConfig {
  [key: string]: SchemaField;
}

interface CareerCardSchema {
  id: string;
  card_type: string;
  display_name: string;
  version: number;
  schema: CardConfig;
  is_active: boolean;
}

interface CareerCardContextType {
  schemas: CareerCardSchema[];
  latestSchema: CareerCardSchema | null;
  isLoading: boolean;
  error: Error | null;
  schemaTypes: string[];
  schemasLoaded: number;
  forceRefresh: () => Promise<void>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CareerCardContext = createContext<CareerCardContextType | undefined>(undefined);

export const CareerCardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const initRef = useRef(false);

  // Use Convex query for schemas - only fetch when authenticated
  // Schemas are public metadata but we gate on auth for consistency
  const convexSchemas = useQuery(
    api.careerCards.getSchemas,
    isAuthenticated ? { activeOnly: true } : "skip"
  );

  // Transform Convex response to match expected interface
  const schemas: CareerCardSchema[] = useMemo(() => {
    if (!convexSchemas) return [];
    return convexSchemas.map((s: { id: string; card_type: string; display_name: string; version: number; schema: unknown; is_active: boolean }) => ({
      id: s.id,
      card_type: s.card_type,
      display_name: s.display_name,
      version: s.version,
      schema: s.schema as CardConfig,
      is_active: s.is_active,
    }));
  }, [convexSchemas]);

  // Derive loading state from Convex query
  const isLoading = authLoading || (isAuthenticated && convexSchemas === undefined);

  // Derive schema types from schemas
  const schemaTypes = useMemo(() => schemas.map(s => s.card_type), [schemas]);
  const schemasLoaded = schemas.length;

  // Error handling - Convex queries throw on error, but we can track undefined states
  const error: Error | null = null;

  // Force refresh function - Convex handles reactivity, but we can provide a no-op
  // or trigger a refetch by temporarily skipping the query
  const forceRefresh = async () => {
    // Convex queries are reactive, so data updates automatically
    // This is kept for API compatibility but is essentially a no-op
    logger.debug('Force refresh called - Convex handles reactivity automatically');
  };

  // Debug logging
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    if (!initRef.current && schemas.length > 0) {
      initRef.current = true;

      // Debug: Log what we're getting from Convex
      if (import.meta.env.DEV) {
        const adHocSchema = schemas.find((s) => s.card_type === 'AdHocWork');
        if (adHocSchema) {
          console.log('AdHocWork schema from Convex:', {
            version: adHocSchema.version,
            fields: Object.keys(adHocSchema.schema),
            hasActiveToDo: 'active_to_do' in adHocSchema.schema,
            hasCompletedTasks: 'completed_tasks' in adHocSchema.schema,
            hasTasks: 'tasks' in adHocSchema.schema
          });
        }
      }
    }

    logger.render({
      schemasLoaded: schemaTypes.length,
      isLoading,
      error,
      schemaTypes
    });
  }, [schemas, schemaTypes, isLoading, error, authLoading, isAuthenticated]);

  return (
    <CareerCardContext.Provider
      value={{
        schemas,
        latestSchema: null,
        isLoading,
        error,
        schemaTypes,
        schemasLoaded,
        forceRefresh
      }}
    >
      {children}
    </CareerCardContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCareerCard = (): CareerCardContextType => {
  const context = useContext(CareerCardContext);
  if (context === undefined) {
    throw new Error('useCareerCard must be used within a CareerCardProvider');
  }
  return context;
};